import { useContext, useMemo } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';
import { usePolicyGroupId } from 'hooks/usePolicyGroupId';
import { useGroupedBillingSync } from 'hooks/useGroupedBillingSync';
import { APIApplicationFile } from 'types/api.types';

import { Flex } from '@chakra-ui/react';

import SharedSubmit from 'components/shared/Submit';
import {
  StepOne,
  StepTwo,
  StepThree,
  GroupedStepOne,
  GroupedStepTwo,
  GroupedStepThree,
} from 'components/shared/steps';

import { apiCreateApplicationLog } from 'api';

import { AuthContext } from 'contexts/AuthContext';

import useIsBinderDisabled from 'views/auto_form/hooks/useIsBinderDisabled';
import isSameAsMailingAddress from 'views/auto_form/sections/Vehicles/helpers/isSameAsMailingAddress';

import {
  changeIsDataSaved,
  setBinderPDF,
} from 'reducers/autoPersonalFormReducer';

import {
  isAtlanticProvince,
  isNonOntarioProvince,
  PROVINCE_ALBERTA,
  PROVINCE_NOVA_SCOTIA,
} from 'variables/constants';

interface SubmitProps {
  additionalFiles?: APIApplicationFile[];
}

const Submit: React.FC<SubmitProps> = ({ additionalFiles = [] }) => {
  const { user } = useContext(AuthContext);
  const dispatch = useAppDispatch();

  const uuid = useAppSelector((state) => state.autoPersonalForm.uuid);
  const policyGroupId = usePolicyGroupId(uuid);

  const { isInMultiBinderGroup } = useGroupedBillingSync(uuid, {
    policyGroupId,
  });

  const data = useAppSelector((state) => state.autoPersonalForm.data);
  const remarks = useAppSelector((state) => state.autoPersonalForm.remarks);
  const files = useAppSelector((state) => state.autoPersonalForm.files);
  const payments = useAppSelector((state) => state.autoPersonalForm.payments);
  const isDataSaved = useAppSelector(
    (state) => state.autoPersonalForm.isDataSaved,
  );
  const missingMandatoryFields = useAppSelector(
    (state) => state.autoPersonalForm.missingMandatoryFields,
  );
  const province = useAppSelector((state) => state.autoPersonalForm.province);
  const isBinderDisabled = useIsBinderDisabled();

  const hasCommercialRatedVehicle = useMemo(() => {
    const isAlberta = province === PROVINCE_ALBERTA;
    if (!isAlberta && !isAtlanticProvince(province)) return false;

    return data.vehicles?.some((vehicle) => {
      const weight = parseFloat(String(vehicle.weight)) || 0;
      const hasExcessWeight = weight > 4500;
      const hasExcessRadius = vehicle.is_operating_radius_over_40km === true;
      return hasExcessWeight || hasExcessRadius;
    });
  }, [province, data.vehicles]);

  const hasCommercialOrFarmUse = useMemo(() => {
    if (!isNonOntarioProvince(province)) return false;

    return data.vehicles?.some(
      (vehicle) => vehicle.is_commercial === true || vehicle.is_farm === true,
    );
  }, [province, data.vehicles]);

  const hasSpecialCircumstances = useMemo(() => {
    if (!isNonOntarioProvince(province)) return false;

    return Boolean(data.report_of_broker_agent?.has_special_circumstances);
  }, [province, data.report_of_broker_agent]);

  const hasResidualMarketTakeAllComers = useMemo(() => {
    if (!isNonOntarioProvince(province)) return false;

    return Boolean(
      data.report_of_broker_agent?.is_residual_market_take_all_comers,
    );
  }, [province, data.report_of_broker_agent]);

  const hasSmapAccessed = useMemo(() => {
    if (!isNonOntarioProvince(province)) return false;

    return Boolean(data.report_of_broker_agent?.is_smap_accessed);
  }, [province, data.report_of_broker_agent]);

  const hasApplicantNotFullOwner = useMemo(() => {
    if (province !== PROVINCE_ALBERTA && province !== PROVINCE_NOVA_SCOTIA)
      return false;

    return data.vehicles_summary?.is_full_onwer === false;
  }, [province, data.vehicles_summary]);

  const hasLienholderOrLessorSameAsMailing = useMemo(() => {
    if (!isNonOntarioProvince(province)) return false;

    return data.vehicles?.some(
      (vehicle) =>
        isSameAsMailingAddress(vehicle.lienholder_info, data.applicant) ||
        isSameAsMailingAddress(vehicle.lessor, data.applicant),
    );
  }, [province, data.vehicles, data.applicant]);

  const hasMissingPaymentDetails = useMemo(() => {
    const selectedPaymentMethod = data.billing?.payment_method;

    // Only check if payment method is EFT or credit card
    if (
      selectedPaymentMethod === 'eft' ||
      selectedPaymentMethod === 'credit_card'
    ) {
      if (!payments || payments.length === 0) return true;

      // Check if there's a payment entry for the selected method in payment_method section
      const hasPaymentEntry = payments.some(
        (payment) =>
          payment.type_name === selectedPaymentMethod &&
          payment.section_name === 'payment_method',
      );

      return !hasPaymentEntry;
    }

    return false;
  }, [data.billing?.payment_method, payments]);

  const hasMissingDownPaymentDetails = useMemo(() => {
    const hasDownPayment = data.billing?.has_down_payment;
    const downPaymentMethod = data.billing?.down_payment_method;

    // Check down payment method if down payment is selected and method is credit card
    if (hasDownPayment && downPaymentMethod === 'credit_card') {
      if (!payments || payments.length === 0) return true;

      // Check if there's a payment entry for down payment
      const hasDownPaymentEntry = payments.some(
        (payment) =>
          payment.type_name === 'credit_card' &&
          payment.section_name === 'down_payment',
      );

      return !hasDownPaymentEntry;
    }

    return false;
  }, [
    data.billing?.has_down_payment,
    data.billing?.down_payment_method,
    payments,
  ]);

  const renderFinalSteps = () => {
    if (isInMultiBinderGroup) {
      return (
        <Flex justify="space-between" align="center" gap="20px">
          <GroupedStepOne formStateKey="autoPersonalForm" />
          <GroupedStepTwo formStateKey="autoPersonalForm" />
          <GroupedStepThree formStateKey="autoPersonalForm" />
        </Flex>
      );
    }

    return (
      <Flex justify="space-between" align="center" gap="20px">
        <StepOne formStateKey="autoPersonalForm" />
        <StepTwo formStateKey="autoPersonalForm" />
        <StepThree formStateKey="autoPersonalForm" />
      </Flex>
    );
  };

  const handleSaveSuccess = () => {
    dispatch(setBinderPDF(null));
  };

  return (
    <SharedSubmit
      formStateKey="autoPersonalForm"
      data={data}
      remarks={remarks}
      files={files}
      additionalFiles={additionalFiles}
      missingMandatoryFields={missingMandatoryFields}
      isDataSaved={isDataSaved}
      isFormDisabled={isBinderDisabled}
      user={user}
      changeIsDataSaved={(value) => dispatch(changeIsDataSaved(value))}
      saveApiCall={(uuid, data, remarks, files, isFormComplete) => {
        return apiCreateApplicationLog(
          uuid,
          data,
          remarks,
          files,
          isFormComplete,
        ).then((result) => {
          handleSaveSuccess();
          return result;
        });
      }}
      renderFinalSteps={renderFinalSteps}
      hasMissingPaymentDetails={hasMissingPaymentDetails}
      hasMissingDownPaymentDetails={hasMissingDownPaymentDetails}
      hasCommercialRatedVehicle={hasCommercialRatedVehicle}
      hasCommercialOrFarmUse={hasCommercialOrFarmUse}
      hasSpecialCircumstances={hasSpecialCircumstances}
      hasResidualMarketTakeAllComers={hasResidualMarketTakeAllComers}
      hasSmapAccessed={hasSmapAccessed}
      hasApplicantNotFullOwner={hasApplicantNotFullOwner}
      hasLienholderOrLessorSameAsMailing={hasLienholderOrLessorSameAsMailing}
    />
  );
};

export default Submit;
